// The animated backgrounds of the old templates, matched to their preview
// renders: a dense still starfield with a few stars twinkling (Orbit,
// Marble, Insta), embers streaking up out of red smoke (Glide's flames),
// and warped flowing colour (Neon). Everything is a function of song time
// and a fixed seed, so the preview and the export draw the same frames.
import { hex, paintNoise } from "./noise";

type Ctx = CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D;

function seeded(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let r = Math.imul(a ^ (a >>> 15), 1 | a);
    r = (r + Math.imul(r ^ (r >>> 7), 61 | r)) ^ r;
    return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
  };
}

function makeCanvas(w: number, h: number) {
  return typeof OffscreenCanvas !== "undefined"
    ? new OffscreenCanvas(w, h)
    : Object.assign(document.createElement("canvas"), { width: w, height: h });
}

// ── stars ──────────────────────────────────────────────────────────────────

const skies = new Map<string, OffscreenCanvas | HTMLCanvasElement>();

/** The still sky: a couple of thousand stars, mostly specks, a few soft bright ones. */
function sky(W: number, H: number) {
  const key = `${W}x${H}`;
  const hit = skies.get(key);
  if (hit) return hit;
  const canvas = makeCanvas(W, H);
  const ctx = canvas.getContext("2d") as Ctx;
  ctx.fillStyle = "#000000";
  ctx.fillRect(0, 0, W, H);
  const rand = seeded(7);
  const k = Math.min(W, H) / 1080;
  const count = Math.round(((W * H) / (1080 * 1920)) * 2400);
  for (let i = 0; i < count; i++) {
    const x = rand() * W,
      y = rand() * H;
    const big = rand() < 0.03;
    const r = (big ? 1.6 + rand() * 1.6 : 0.5 + rand() * rand() * 1.3) * k;
    const a = big ? 0.75 + rand() * 0.25 : 0.25 + rand() * 0.6;
    if (big) {
      const g = ctx.createRadialGradient(x, y, 0, x, y, r * 3);
      g.addColorStop(0, `rgba(255,255,255,${a})`);
      g.addColorStop(0.35, `rgba(255,255,255,${a * 0.5})`);
      g.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = g;
      ctx.fillRect(x - r * 3, y - r * 3, r * 6, r * 6);
    } else {
      ctx.fillStyle = `rgba(${220 + rand() * 35}, ${225 + rand() * 30}, 255, ${a})`;
      ctx.fillRect(x, y, r * 1.6, r * 1.6);
    }
  }
  skies.set(key, canvas);
  return canvas;
}

type Twinkle = {
  x: number;
  y: number;
  r: number;
  phase: number;
  speed: number;
};
const TWINKLES: Twinkle[] = (() => {
  const rand = seeded(19);
  return Array.from({ length: 70 }, () => ({
    x: rand(),
    y: rand(),
    r: 1 + rand() * 1.8,
    phase: rand() * Math.PI * 2,
    speed: 0.8 + rand() * 2,
  }));
})();

export function drawStars(ctx: Ctx, W: number, H: number, t: number) {
  ctx.drawImage(sky(W, H), 0, 0, W, H);
  const k = Math.min(W, H) / 1080;
  for (const s of TWINKLES) {
    const a = Math.max(0, Math.sin(t * s.speed + s.phase));
    if (a < 0.05) continue;
    const x = s.x * W,
      y = s.y * H,
      r = s.r * k;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r * 4);
    g.addColorStop(0, `rgba(255,255,255,${a})`);
    g.addColorStop(0.3, `rgba(255,255,255,${a * 0.4})`);
    g.addColorStop(1, "rgba(255,255,255,0)");
    ctx.fillStyle = g;
    ctx.fillRect(x - r * 4, y - r * 4, r * 8, r * 8);
  }
}

// ── flames ─────────────────────────────────────────────────────────────────

type Spark = {
  x: number;
  y: number;
  len: number;
  w: number;
  speed: number;
  drift: number;
  phase: number;
  hot: number;
};
const SPARKS: Spark[] = (() => {
  const rand = seeded(11);
  return Array.from({ length: 220 }, () => {
    const low = rand() < 0.6;
    return {
      x: rand(),
      y: low ? 0.6 + rand() * 0.45 : rand(),
      len: 4 + rand() * 18,
      w: 1.5 + rand() * 2.5,
      speed: 0.05 + rand() * 0.16,
      drift: (rand() - 0.35) * 0.08,
      phase: rand() * Math.PI * 2,
      hot: rand(),
    };
  });
})();
const SMOKE = ["#000000", "#1A0302", "#4A0A04", "#8A1E08", "#C2410C"].map(hex);

export function drawEmbers(ctx: Ctx, W: number, H: number, t: number) {
  ctx.fillStyle = "#050101";
  ctx.fillRect(0, 0, W, H);
  // Red smoke, heavy at the bottom and gone by the top third.
  paintNoise(
    ctx,
    W,
    H,
    t * 0.35,
    SMOKE,
    3.2,
    (v, y) => Math.max(0, Math.min(1, (y - 0.35) / 0.55)) * (0.35 + v * 0.65),
  );
  // A warm haze low in the frame and a soft out-of-focus light at the side.
  const haze = ctx.createRadialGradient(
    W * 0.6,
    H * 1.02,
    0,
    W * 0.6,
    H * 1.02,
    H * 0.55,
  );
  haze.addColorStop(0, "rgba(255, 110, 30, 0.55)");
  haze.addColorStop(1, "rgba(255, 80, 10, 0)");
  ctx.fillStyle = haze;
  ctx.fillRect(0, 0, W, H);
  const bx = W * 0.96,
    by = H * 0.57,
    br = Math.min(W, H) * 0.09 * (1 + 0.06 * Math.sin(t * 1.4));
  const bokeh = ctx.createRadialGradient(bx, by, 0, bx, by, br);
  bokeh.addColorStop(0, "rgba(255, 140, 50, 0.85)");
  bokeh.addColorStop(0.6, "rgba(240, 100, 30, 0.45)");
  bokeh.addColorStop(1, "rgba(240, 90, 20, 0)");
  ctx.fillStyle = bokeh;
  ctx.fillRect(bx - br, by - br, br * 2, br * 2);

  // Sparks: short bright streaks along the way they're flying.
  const k = Math.min(W, H) / 1080;
  ctx.save();
  ctx.globalCompositeOperation = "lighter";
  ctx.lineCap = "round";
  for (const s of SPARKS) {
    const p = (((s.y - t * s.speed) % 1.1) + 1.1) % 1.1;
    const x =
      (((s.x + t * s.drift + Math.sin(t * 1.3 + s.phase) * 0.01) % 1) + 1) % 1;
    const life =
      Math.min(1, (p - 0.02) * 3) * (0.55 + 0.45 * Math.sin(t * 7 + s.phase));
    if (life <= 0) continue;
    const vx = s.drift,
      vy = -s.speed;
    const n = Math.hypot(vx, vy) || 1;
    const len = s.len * k * (1.2 + s.hot);
    ctx.strokeStyle = `rgba(255, ${150 + s.hot * 80}, ${40 + s.hot * 60}, ${life})`;
    ctx.lineWidth = s.w * k;
    ctx.beginPath();
    ctx.moveTo(x * W, p * H);
    ctx.lineTo(x * W - (vx / n) * len, p * H - (vy / n) * len);
    ctx.stroke();
  }
  ctx.restore();
}

// ── neon ───────────────────────────────────────────────────────────────────

// Measured off the old Neon render: soft pastels, never fully saturated.
const NEON = [
  "#7C6FD8",
  "#B48BDB",
  "#E57FB6",
  "#EE8FC4",
  "#E7788F",
  "#9CC9D8",
  "#E46AA4",
].map(hex);

export function drawFluid(ctx: Ctx, W: number, H: number, t: number) {
  paintNoise(ctx, W, H, t * 0.4, NEON, 1.1, undefined, 2.4, 1.5, 2.5);
}
